import React, { useState, useEffect, useRef } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Button} from "react-bootstrap";
import GridItem from "../../../../Components/UiComponents/GridItem/GridItem";
import { ArrowLeft } from "lucide-react";
import FormHandler from "../../Common/Form/FormHandler/FormHandler";
import MultiSelectWrapper from "../../Common/Form/MultiSelect/MultiSelect";
import { getData, postDataWithBody, putDataWithBody } from "../../../../Services/Services";
import * as EndPoints from "../../../../Services/EndPoints";
import { CategoryMatrixFormConfig, categoryMatrixSchema } from "../../../../JsonData/IncidentCategoryMatrixConfig";

const IncidentCategoryMatrixForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.editData || null;
  console.log("Edit Data received in form:", editData);
  const viewMode = location.state?.viewMode || false;
  const [stateId, setStateId] = useState(null);
  const [soPConfig, setSoPConfig] = useState(CategoryMatrixFormConfig);
  const [allFetchedResources, setAllFetchedResources] = useState([]);
  const [existingCombinations, setExistingCombinations] = useState([]);
  const initializationRef = useRef(false);
  const [isResourceTableExpanded, setIsResourceTableExpanded] = useState(true);

  useEffect(() => {
    if (!stateId) return;
    // Fetch existing combinations for duplicate validation. The active-matrix
    // endpoint already returns one grouped record per hazardType/severityLevel
    // combination that is currently active, so no further grouping/filtering
    // by is_deleted is needed here.
    const fetchExisting = async () => {
      try {
        const resp = await getData(EndPoints.getActiveCategoryMatrixList.replace("STATEID", stateId));
        if (resp && Array.isArray(resp)) {
          const unique = resp.map((item) => ({
            hazardType: item.hazardType,
            severityLevel: item.severityLevel,
          }));
          setExistingCombinations(unique);
        }
      } catch (error) {
        console.error("Error fetching existing combinations:", error);
      }
    };
    fetchExisting();
  }, [stateId]);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(categoryMatrixSchema),
    defaultValues: {
      resource_items: [],
    },
  });

  const { fields: resourceFields, append: appendResource, remove: removeResource } = useFieldArray({
    control,
    name: "resource_items",
  });

  const selectedCategory = watch("incident_category");
  const selectedSeverity = watch("severity");
  const selectedResource = watch("sop_resource");

  const isFormReady = !!selectedCategory && !!selectedSeverity && resourceFields.length > 0;

  // Fetch State ID on load
  useEffect(() => {
    const fetchStateId = async () => {
      try {
        const userProfile = await getData(EndPoints.getThemeURL);
        const sId = userProfile[0]?.state_id;
        setStateId(sId);
      } catch (error) {
        console.error("Error fetching state ID:", error);
      }
    };
    fetchStateId();
  }, []);

  // Fetch Incident Categories using the same logic as SOPForm
 // Fetch Incident Categories using the same logic as SOPForm
useEffect(() => {
  if (!stateId) return;

  const fetchCategories = async () => {
    try {
      const res = await postDataWithBody(
        `${EndPoints.getTaskConfigsURL}/${stateId}`,
        ["INCDNT_CAT"],
        {}
      );

      if (res && Array.isArray(res)) {
        const categoryOptions = res.map((config) => ({
          label: config.value,
          value: config.id.toString(),
        }));

        setSoPConfig((prev) => {
          const updated = JSON.parse(JSON.stringify(prev));
          if (updated[0]?.fields) {
            // Update incident_category field (index 0)
            if (updated[0].fields[0]) {
              updated[0].fields[0].options = categoryOptions;
              updated[0].fields[0].disabled = !!editData || viewMode;
            }
            
            // Update severity field (index 1) - THIS IS THE KEY CHANGE
            if (updated[0].fields[1]) {
              updated[0].fields[1].disabled = !!editData || viewMode;
            }
            
            // Update resource field (index 2) for view mode only
            if (updated[0].fields[2]) {
              updated[0].fields[2].disabled = viewMode;
            }
          }
          return updated;
        });
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };
  fetchCategories();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [stateId, editData, viewMode]); // Add editData and viewMode as dependencies

  // Fetch Resources when Category is selected
  // Note: Matrix form uses a general resource fetch for now as it's a mapping configuration
  useEffect(() => {
    const fetchResources = async () => {
      if (selectedCategory && stateId) {
        try {
          // Region ID 5004 is Ladakh which is often used as a parent for these lists
          const defaultRegionId = 5004;
          const resourceURL = `${EndPoints.getResourcesBasedOnRegion}/${defaultRegionId}`;

          console.log("resoos,",resourceURL)
          const resources = await getData(EndPoints.getResourcesBasedOnRegion);
          setAllFetchedResources(resources || []);

          if (resources && Array.isArray(resources)) {
            const resourceOptions = resources.map((r) => ({
              label: r.agency_name ? `${r.name} (${r.agency_name})` : r.name,
              value: r.id.toString(),
            }));

            setSoPConfig((prev) => {
              const updated = JSON.parse(JSON.stringify(prev));
              if (updated[0]?.fields && updated[0].fields[2]) {
                updated[0].fields[2].options = resourceOptions;
              }
              return updated;
            });
          }
        } catch (error) {
          console.error("Error fetching resources:", error);
        }
      }
    };
    fetchResources();
  }, [selectedCategory, stateId]);
  // Populate Form in Edit Mode
  useEffect(() => {
    if (editData && soPConfig[0]?.fields?.[0]?.options?.length > 0 && !initializationRef.current) {
      console.log("Initializing Edit Mode with data:", editData);

      // 1. Map hazardType label back to ID
      const catOptions = soPConfig[0].fields[0].options;
      const matchingCat = catOptions.find(opt => opt.label === editData.hazardType);

      if (matchingCat) {
        setValue("incident_category", matchingCat.value);
      }

      // 2. Set Severity
      setValue("severity", editData.severityLevel);

      // 3. Map rawResources to MultiSelect and FieldArray
      if (editData.rawResources && editData.rawResources.length > 0) {
        const selectedResourcesForDropdown = editData.rawResources.map(res => ({
          label: res.resource_name,
          value: res.resource_id?.toString() || res.resource_name
        }));

        setValue("sop_resource", selectedResourcesForDropdown);

        // Directly append resources to the field array
        editData.rawResources.forEach(res => {
          appendResource({
            resource_id: res.resource_id?.toString() || res.resource_name,
            name: res.resource_name,
            agency_name: "",
            available_quantity: res.available_quantity ?? 0,
            quantity: res.required_quantity || 1,
            cum_deployed_quantity: res.cum_deployed_quantity ?? 0,
            cum_damaged_quantity: res.cum_damaged_quantity ?? 0,
            cum_available_quantity: res.cum_available_quantity ?? 0,
            cum_total_quantity: res.cum_total_quantity ?? 0
          });
        });
      }

      initializationRef.current = true;
     
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData, soPConfig[0]?.fields?.[0]?.options, setValue, appendResource]);

  // Synchronize MultiSelect with Table
  useEffect(() => {
    if (!allFetchedResources.length) return;
    if (!initializationRef.current && editData) return; // wait until edit init is done

    const currentSelectedResources = Array.isArray(selectedResource) ? selectedResource : [];
    const currentSelectedIds = currentSelectedResources.map((r) => r.value.toString());
    const currentTableIds = resourceFields.map((f) => f.resource_id.toString());

    currentSelectedResources.forEach((sel) => {
      const selId = sel.value.toString();
      if (!currentTableIds.includes(selId)) {
        const resourceData = allFetchedResources.find((r) => r.id.toString() === selId);
        if (resourceData) {
          appendResource({
            resource_id: selId,
            name: resourceData.name,
            agency_name: resourceData.agency_name,
            available_quantity: resourceData.available_quantity,
            quantity: 1,
            cum_available_quantity: resourceData.cum_available_quantity ?? 0,
            cum_damaged_quantity: resourceData.cum_damaged_quantity ?? 0,
            cum_deployed_quantity: resourceData.cum_deployed_quantity ?? 0,
            cum_total_quantity: resourceData.cum_total_quantity ?? 0,
          });
        }
      }
    });

    const indicesToRemove = [];
    resourceFields.forEach((field, index) => {
      if (!currentSelectedIds.includes(field.resource_id.toString())) {
        indicesToRemove.push(index);
      }
    });
    if (indicesToRemove.length > 0) {
      indicesToRemove.sort((a, b) => b - a).forEach((idx) => removeResource(idx));
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedResource, allFetchedResources, resourceFields, appendResource, removeResource]);

  // const handleRemoveFromTable = (index) => {
  //   const fieldToRemove = resourceFields[index];
  //   const resourceId = fieldToRemove.resource_id;
  //   const currentSelection = getValues("sop_resource") || [];
  //   const newSelection = currentSelection.filter((item) => item.value.toString() !== resourceId.toString());
  //   setValue("sop_resource", newSelection);
  // };

  const submitHandler = async (data) => {
    try {
      // Find labels from options as Select only returns the ID/Value
      const catOptions = soPConfig[0]?.fields[0]?.options || [];
      const sevOptions = soPConfig[0]?.fields[1]?.options || [];

      const selectedCat = catOptions.find(opt => opt.value.toString() === data.incident_category.toString());
      const selectedSev = sevOptions.find(opt => opt.value.toString() === data.severity.toString());

      const hazardType = selectedCat ? selectedCat.label : data.incident_category;
      const severityLevel = selectedSev ? selectedSev.label : data.severity;

      // Duplicate combination check (skip in edit mode)
      if (!editData) {
        const isDuplicate = existingCombinations.some(
          combo => combo.hazardType === hazardType && combo.severityLevel === severityLevel
        );
        if (isDuplicate) {
          toast.error(`Combination "${hazardType} - ${severityLevel}" already exists. Please select a different combination.`, {
            position: "top-center",
            toastId: "duplicate-combo-toast"
          });
          return;
        }
      }

      const payload = {
        state_id: stateId,
        hazardType: hazardType,
        severityLevel: severityLevel,
        resources: data.resource_items.map((item) => ({
          resourceId: parseInt(item.resource_id),
          resourceName: item.name,
          requiredQuantity: parseInt(item.quantity),
          availableQuantity: parseInt(item.available_quantity) || 0,
        })),
      };


      let res;
      if (editData) {
        // For update, the API might expect a slightly different format or just the same payload
        res = await putDataWithBody(EndPoints.updateMatrixResource, payload, {});
      } else {
        res = await postDataWithBody(EndPoints.saveCategoryMatrix, payload, {});
      }

      if (res.status === "success") {
        toast.success(
          res.msg || (editData ? "Matrix updated successfully" : "Matrix saved successfully"),
          {
            position: "top-center" // or "top-center", "top-left"
          }
        );
        navigate("..");
      } else {
        toast.error(res.msg || "Error saving matrix", {
          position: "top-center"
        });
      }
    } catch (error) {
      toast.error("Error saving matrix");
      console.error(error);
    }
  };

  const GenerateForm = (fields, register, errors, control) => {
    const resourceField = fields.find((f) => f.name === "sop_resource");
    const nonResourceFields = fields.filter((f) => f.name !== "sop_resource");
    const selectedResourceCount = Array.isArray(selectedResource) ? selectedResource.length : 0;

    const infoLabelStyle = {
      color: "#ffa100",
      fontWeight: "600",
      fontSize: "12px",
      marginBottom: "4px",
      textTransform: "uppercase",
      letterSpacing: "0.4px",
    };
    const infoValueStyle = {
      fontSize: "14px",
      fontWeight: "500",
      color: "#1a2e35",
    };

    return (
      <div>
        {viewMode ? (
          /* ── View Mode: plain info display ── */
          <div className="px-3">
            <div className="row mb-3">
              <div className="col-sm-4">
                <div style={infoLabelStyle}>Source ID</div>
                <div style={infoValueStyle}>{editData?.src_seq_id || "N/A"}</div>
              </div>
              <div className="col-sm-4">
                <div style={infoLabelStyle}>Incident Category</div>
                <div style={infoValueStyle}>{editData?.hazardType || "N/A"}</div>
              </div>
              <div className="col-sm-4">
                <div style={infoLabelStyle}>Severity</div>
                <div style={infoValueStyle}>{editData?.severityLevel || "N/A"}</div>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-sm-12">
                <div style={infoLabelStyle}>Resource List</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "4px" }}>
                  {(editData?.rawResources || []).map((res, idx) => (
                    <span key={idx} style={{
                      background: "#f0f4f8",
                      border: "1px solid #c8dce8",
                      borderRadius: "6px",
                      padding: "4px 12px",
                      fontSize: "13px",
                      color: "#1a2e35",
                      fontWeight: "500",
                    }}>
                      {res.resource_name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Category + Severity fields */}
            <FormHandler
              fields={nonResourceFields}
              register={register}
              errors={errors}
              control={control}
              columns={2}
              setValue={setValue}
              getValues={getValues}
              watch={watch}
            />

            {/* Resource list field with count badge + Clear All */}
            {resourceField && (
              <div className="px-3 mt-3" style={{ maxWidth: "600px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <label style={{ color: "black", marginBottom: 0, fontWeight: "500", fontSize: "13px" }}>
                      {t(resourceField.label)}
                    </label>
                    {selectedResourceCount > 0 && (
                      <span style={{
                        background: "#1C4E72",
                        color: "#fff",
                        fontSize: "11px",
                        fontWeight: "600",
                        borderRadius: "20px",
                        padding: "2px 12px",
                        letterSpacing: "0.3px",
                      }}>
                        {selectedResourceCount} {selectedResourceCount === 1 ? "resource" : "resources"} selected
                      </span>
                    )}
                  </div>
                  {selectedResourceCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setValue("sop_resource", [])}
                      style={{
                        background: "#fff",
                        color: "#c0392b",
                        border: "1.5px solid #c0392b",
                        borderRadius: "6px",
                        padding: "3px 14px",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = "#c0392b"; e.currentTarget.style.color = "#fff"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#c0392b"; }}
                    >
                      Clear All
                    </button>
                  )}
                </div>
                <div className="matrix-resource-select" style={{ position: "relative" }}>
                  <style>{`
                    .matrix-resource-select .react-select__clear-indicator { display: none; }
                    .matrix-resource-select .react-select__indicator-separator { display: none; }
                  `}</style>
                  <MultiSelectWrapper
                    name="sop_resource"
                    label=""
                    errors={errors}
                    options={resourceField.options}
                    control={control}
                    disabled={resourceField.disabled}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {/* Selected resources table */}
        {resourceField && resourceFields.length > 0 && (
          <div className="px-3 mt-4">
          <style>{`.icm-chevron-icon { color: #ffffff !important; }`}</style>
            <div style={{ borderRadius: "10px", overflow: "hidden", border: "1px solid #c8dce8" }}>
              <table className="table table-sm mb-0" style={{ borderCollapse: "collapse" }}>
                <thead>
                  <tr
                    onClick={() => setIsResourceTableExpanded(prev => !prev)}
                    style={{ cursor: "pointer" }}
                  >
                    <th className="ps-3 py-2" style={{ backgroundColor: "#1C4E72", border: "none", color: "#fff", fontWeight: "700", fontSize: "13px" }}>
                      {t("sopformlabels.selectedresources") || "Selected Resources for SOP"}
                      <span style={{ marginLeft: "10px", background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: "11px", fontWeight: "600", borderRadius: "20px", padding: "2px 10px", border: "1px solid rgba(255,255,255,0.3)" }}>
                        {resourceFields.length} {resourceFields.length === 1 ? "Resource" : "Resources"}
                      </span>
                    </th>
                    <th className="text-center py-2" style={{ backgroundColor: "#1C4E72", border: "none", color: "#fff", fontWeight: "400", fontSize: "12px", width: "110px", fontStyle: "normal" }}>Available Qty</th>
                    <th className="text-center py-2" style={{ backgroundColor: "#1C4E72", border: "none", color: "#fff", fontWeight: "400", fontSize: "12px", width: "110px", fontStyle: "normal" }}>Damaged Qty</th>
                    <th className="text-center py-2" style={{ backgroundColor: "#1C4E72", border: "none", color: "#fff", fontWeight: "400", fontSize: "12px", width: "110px", fontStyle: "normal" }}>Deployed Qty</th>
                    <th className="text-center py-2" style={{ backgroundColor: "#1C4E72", border: "none", color: "#fff", fontWeight: "400", fontSize: "12px", width: "110px", fontStyle: "normal" }}>Total Qty</th>
                    <th className="text-center py-2" style={{ backgroundColor: "#1C4E72", border: "none", color: "#fff", fontWeight: "400", fontSize: "13px", width: "160px", fontStyle: "normal" }}>
                      <span style={{ marginRight: "8px" }}>Qty To Deploy</span>
                      <i className={`fas fa-chevron-${isResourceTableExpanded ? "up" : "down"} icm-chevron-icon`} style={{ fontSize: "12px" }}></i>
                    </th>
                  </tr>
                </thead>
                {isResourceTableExpanded && <tbody>
                  {resourceFields.map((field, index) => {
                    const rowBg = index % 2 === 0 ? "#dff2f2" : "#f5fafa";
                    const hoverBg = "#c8e8e8";
                    return (
                    <tr
                      key={field.id}
                      className="align-middle"
                      onMouseEnter={e => {
                        e.currentTarget.querySelectorAll("td").forEach(td => td.style.backgroundColor = hoverBg);
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.querySelectorAll("td").forEach(td => td.style.backgroundColor = rowBg);
                      }}
                    >
                      <td className="ps-3 py-2" style={{ backgroundColor: rowBg, borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: "1px solid #d4e8ec" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <span style={{ fontWeight: "600", fontSize: "12px", color: "#1C4E72", minWidth: "20px" }}>
                            {index + 1}.
                          </span>
                          <span style={{ fontWeight: "500", fontSize: "13px", color: "#1a2e35" }}>{field.name}</span>
                        </div>
                      </td>
                      <td className="text-center py-2" style={{ backgroundColor: rowBg, borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: "1px solid #d4e8ec", fontSize: "13px", color: "#1a2e35" }}>
                        {field.cum_available_quantity ?? 0}
                      </td>
                      <td className="text-center py-2" style={{ backgroundColor: rowBg, borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: "1px solid #d4e8ec", fontSize: "13px", color: "#1a2e35" }}>
                        {field.cum_damaged_quantity ?? 0}
                      </td>
                      <td className="text-center py-2" style={{ backgroundColor: rowBg, borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: "1px solid #d4e8ec", fontSize: "13px", color: "#1a2e35" }}>
                        {field.cum_deployed_quantity ?? 0}
                      </td>
                      <td className="text-center py-2" style={{ backgroundColor: rowBg, borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: "1px solid #d4e8ec", fontSize: "13px", color: "#1a2e35" }}>
                        {field.cum_total_quantity ?? 0}
                      </td>
                      <td className="text-center py-2" style={{ backgroundColor: rowBg, borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: "1px solid #d4e8ec" }}>
                        <input
                          type="number"
                          className="form-control form-control-sm mx-auto"
                          style={{
                            width: "90px",
                            textAlign: "center",
                            border: errors.resource_items?.[index]?.quantity ? "1.5px solid #e74c3c" : "1.5px solid #18a3a0",
                            borderRadius: "6px",
                            fontSize: "13px",
                            fontWeight: "500",
                            color: "#1a2e35",
                            background: viewMode ? "#f0f4f4" : "#fff",
                            padding: "4px 6px",
                          }}
                          title={errors.resource_items?.[index]?.quantity?.message || ""}
                          min={1}
                          onKeyDown={(e) => {
                            if (["-", "+", "e", "E", "."].includes(e.key)) e.preventDefault();
                          }}
                          {...register(`resource_items.${index}.quantity`, {
                            required: "Quantity is required",
                            min: { value: 1, message: "Must be >= 1" },
                            validate: (value) => {
                              const parsed = parseInt(value);
                              if (!parsed || parsed < 1) return "Must be >= 1";
                              const available = parseInt(field.available_quantity) || 0;
                              return parsed <= available || `Cannot exceed ${available}`;
                            }
                          })}
                          disabled={viewMode}
                          defaultValue={field.quantity}
                        />
                        {errors.resource_items?.[index]?.quantity && (
                          <div style={{ color: "#e74c3c", fontSize: "10px", marginTop: "3px" }}>
                            {errors.resource_items[index].quantity.message}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                  })}
                </tbody>}
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const layoutData = [
    {
      x: 0,
      y: 0,
      width: 12,
      height: 13,
      id: 1,
      component: (
        <div className="mt-4 facility-container">
          <div className="form-body">
            <div className="registration__form--body">
              <form onSubmit={handleSubmit(submitHandler)}>
                {soPConfig.map((item, index) => (
                  <div key={index}>
                    {GenerateForm(item.fields, register, errors, control)}
                  </div>
                ))}
                {!viewMode && (
                  <div className="mt-4 d-flex justify-content-center">
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={!isFormReady}
                      title={!isFormReady ? "Please complete all required fields and add at least one resource" : ""}
                      style={{
                        padding: "10px 36px",
                        fontSize: "15px",
                        fontWeight: "600",
                        borderRadius: "8px",
                        minWidth: "170px",
                        opacity: !isFormReady ? 0.55 : 1,
                        cursor: !isFormReady ? "not-allowed" : "pointer",
                        transition: "opacity 0.2s, box-shadow 0.2s",
                        boxShadow: isFormReady ? "0 4px 12px rgba(28,78,114,0.25)" : "none",
                        pointerEvents: !isFormReady ? "none" : "auto",
                      }}
                    >
                      {t("saveAndSubmit")}
                    </Button>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700;800&display=swap');`}</style>
      <div style={{ margin: "8px", fontFamily: "'Poppins', 'Segoe UI', sans-serif" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "10px 14px",
            borderRadius: "10px",
            background: "linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%)",
            boxShadow: "0 2px 6px rgba(46,109,164,0.2)",
          }}
        >
          <ArrowLeft
            size={18}
            color="#fff"
            className="cursor-pointer"
            onClick={() => navigate(-1)}
          />
          <div style={{ fontSize: "14px", fontWeight: 800, color: "#fff", letterSpacing: ".2px" }}>
            {viewMode ? t("View Incident Category Matrix") : editData ? t("Edit Incident Category Matrix") : t("categorymatrixformlabels.header")}
          </div>
        </div>
        <div style={{ marginTop: "10px", backgroundColor: "#fff", borderRadius: "10px" }}>
          <div className="grid-stack grid-stack-instance-1 smartpole__header--transition">
            <GridItem layoutData={layoutData} />
          </div>
        </div>
      </div>
    </>
  );
};

export default IncidentCategoryMatrixForm;
